"use client";

import { NavBadge } from "@/components/chrome/NavBadge";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState, type RefObject } from "react";
import type { MegaMenu } from "@/design-data/nav-menus";
import { CurrencySelector } from "./CurrencySelector";
import { HelplineCompactButton } from "./HelplineButton";
import { HomeBadge } from "./HomeBadge";
import { ThemeToggle } from "./ThemeToggle";
import { onHomeClick } from "./home-click";
import { SearchSuggestInput } from "@/components/search/SearchSuggestInput";

function pathOnly(href: string): string {
  return href.split(/[?#]/)[0] || "/";
}

/**
 * Mobile/tablet navigation panel, mounted only while open (SiteNav owns the
 * open state). Mounting is what switches on the page-level side effects below,
 * and unmounting undoes them, so a close from anywhere — a link, Escape, the
 * toggle, a route change — always restores the page.
 */
export function MobileNav({
  id,
  menus,
  activeKey,
  pathname,
  cartCount = 0,
  isInternational,
  onClose,
  returnFocusRef,
}: {
  id: string;
  menus: MegaMenu[];
  activeKey: string | null;
  pathname: string;
  /** Items in the visitor's trip, shown beside My Trip. */
  cartCount?: number;
  /** Decides which helpline number this visitor is offered. */
  isInternational?: boolean;
  onClose: () => void;
  returnFocusRef: RefObject<HTMLButtonElement | null>;
}) {
  const router = useRouter();
  const panelRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  // The section the visitor is already in starts expanded — the most likely
  // group they want to move around in.
  const [expanded, setExpanded] = useState<string | null>(activeKey);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const returnTo = returnFocusRef.current;

    // Lock the page behind the menu. The attribute also takes the phone
    // discovery bar down: the menu already lists those destinations, and two
    // navigation layers on one screen is exactly what this phase avoids.
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";
    root.setAttribute("data-menu-open", "");

    // Take everything outside the header out of the tab order and the
    // accessibility tree while the menu is open, so keyboard and screen-reader
    // users stay inside the header (toggle + menu) instead of wandering into
    // the page underneath.
    const header = panel.closest("header");
    const madeInert: HTMLElement[] = [];
    for (const el of Array.from(header?.parentElement?.children ?? [])) {
      if (el === header || !(el instanceof HTMLElement) || el.inert) continue;
      el.inert = true;
      madeInert.push(el);
    }

    // Fill exactly the space below the panel's own top edge — a fixed height,
    // not just a max, so a short menu still covers the locked page instead of
    // leaving it showing underneath. dvh follows the mobile browser toolbar
    // showing/hiding, where 100vh overshoots it.
    function fit() {
      if (!panel) return;
      const top = Math.max(0, panel.getBoundingClientRect().top);
      panel.style.height = `calc(100dvh - ${top}px)`;
    }
    fit();
    window.addEventListener("resize", fit);

    panel.focus({ preventScroll: true });

    return () => {
      window.removeEventListener("resize", fit);
      root.style.overflow = prevOverflow;
      root.removeAttribute("data-menu-open");
      for (const el of madeInert) el.inert = false;
      returnTo?.focus({ preventScroll: true });
    };
  }, [returnFocusRef]);

  function runSearch() {
    const q = query.trim();
    if (!q) return;
    onClose();
    router.push(`/search?q=${encodeURIComponent(q)}`);
  }

  return (
    <div
      ref={panelRef}
      id={id}
      tabIndex={-1}
      // Bottom padding clears the fixed chat/WhatsApp buttons (they sit above
      // the header's z-index), so the last items can scroll out from under them.
      className="min-[1180px]:hidden overflow-y-auto overscroll-contain border-t border-[var(--chrome-border)] bg-[var(--chrome-bg-solid)] px-5 pt-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] outline-none"
    >
      <form
        role="search"
        className="mb-3 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          runSearch();
        }}
      >
        <SearchSuggestInput
          type="search"
          aria-label="Search the site"
          placeholder="Search destinations, resorts, tours…"
          value={query}
          onValueChange={setQuery}
          onPicked={() => {
            setQuery("");
            onClose();
          }}
          className="h-12 min-w-0 flex-1 rounded border border-[var(--chrome-border)] bg-[var(--chrome-surface)] px-3 text-base text-[var(--chrome-fg)] outline-none focus:border-[var(--cta)]"
        />
        <button
          type="submit"
          aria-label="Search"
          className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded bg-[var(--cta)] text-[#2b2b2b] cursor-pointer"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.8" />
            <path d="m16 16 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      </form>

      <nav aria-label="Main">
        <ul className="m-0 list-none p-0">
          {menus.map((menu) => {
            const links = menu.columns.flatMap((c) => c.links);
            const hasMenu = links.length > 0;
            const isOpen = expanded === menu.key;
            const isActive = activeKey === menu.key;
            const groupId = `${id}-${menu.key}`;
            return (
              <li key={menu.key} className="border-b border-[var(--chrome-border-soft)]">
                <div className="flex items-stretch">
                  <Link
                    href={menu.href}
                    onClick={(e) => {
                      onHomeClick(e, pathname, menu.href);
                      onClose();
                    }}
                    aria-current={pathname === menu.href ? "page" : undefined}
                    aria-label={menu.key === "home" ? menu.label : undefined}
                    className={`group flex min-h-12 flex-1 items-center text-sm font-semibold tracking-wide uppercase no-underline ${
                      isActive ? "text-[var(--cta)]" : "text-[var(--chrome-fg)]"
                    }`}
                  >
                    {menu.key === "home" ? <HomeBadge /> : menu.label}
                    {menu.badge ? <NavBadge text={menu.badge} /> : null}
                  </Link>
                  {hasMenu ? (
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={groupId}
                      aria-label={`${menu.label} pages`}
                      onClick={() => setExpanded((k) => (k === menu.key ? null : menu.key))}
                      className="-mr-2 inline-flex w-12 shrink-0 cursor-pointer items-center justify-center bg-transparent text-[var(--chrome-muted)]"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                        className={`transition-transform duration-200 motion-reduce:transition-none ${isOpen ? "rotate-180" : ""}`}
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>
                  ) : null}
                </div>
                {hasMenu ? (
                  <ul id={groupId} hidden={!isOpen} className="m-0 list-none pb-2 pl-3">
                    {links.map((link) => {
                      const current = pathname === pathOnly(link.href);
                      return (
                        <li key={`${link.href}::${link.label}`}>
                          <Link
                            href={link.href}
                            onClick={onClose}
                            aria-current={current ? "page" : undefined}
                            className={`flex min-h-11 items-center text-[15px] no-underline ${
                              current ? "text-[var(--cta)]" : "text-[var(--chrome-muted)]"
                            }`}
                          >
                            {link.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </nav>

      <Link href="/plan" onClick={onClose} className="pbd-btn-primary mt-5 flex min-h-12 w-full items-center justify-center">
        Plan Your Trip
      </Link>

      <Link
        href="/cart"
        onClick={onClose}
        className="mt-3 flex min-h-12 w-full items-center justify-center gap-2 rounded border border-[var(--chrome-border)] text-sm font-semibold text-[var(--chrome-fg)] no-underline"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M6 8h12l-1 11H7L6 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M9.5 8V6.5a2.5 2.5 0 0 1 5 0V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        My Trip
        {cartCount > 0 ? (
          <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-[var(--cta)] px-1.5 text-[11px] font-bold text-[#2b2b2b]">
            {cartCount}
          </span>
        ) : null}
      </Link>

      <Link
        href="/account"
        onClick={onClose}
        className="mt-3 flex min-h-12 w-full items-center justify-center gap-2 rounded border border-[var(--chrome-border)] text-sm font-semibold text-[var(--chrome-fg)] no-underline"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5" />
          <path d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        Account / Sign in
      </Link>

      {/* The header keeps the theme toggle on desktop only — below 1180px the
          row is logo, search and menu, so the toggle lives here. */}
      <div className="mt-5 flex min-h-12 items-center justify-between">
        <p className="m-0 text-[10px] font-bold tracking-[0.16em] uppercase text-[var(--cta)]">Theme</p>
        <ThemeToggle />
      </div>

      <div className="mt-5">
        <p className="m-0 mb-2 text-[10px] font-bold tracking-[0.16em] uppercase text-[var(--cta)]">Currency</p>
        <CurrencySelector />
      </div>

      {/* Moved out of the header, not removed: the phone row is logo, search
          and menu, and a guest who wants to call should still find it here. */}
      <div className="mt-5">
        <p className="m-0 mb-2 text-[10px] font-bold tracking-[0.16em] uppercase text-[var(--cta)]">Talk to us</p>
        <HelplineCompactButton isInternational={isInternational} />
        <Link
          href="/contact"
          onClick={onClose}
          className="mt-3 flex min-h-11 items-center text-[15px] text-[var(--chrome-muted)] no-underline"
        >
          Contact &amp; customer assistance
        </Link>
      </div>
    </div>
  );
}
